if (!customElements.get('wnc-recommendation-card')) {
  class WncRecommendationCard extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.initialized = false;
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      const config = this.querySelector('[data-wnc-recommendation-config]');
      this.form = this.querySelector('.wnc-recommendation-card__form');
      if (!config || !this.form) return;
      this.form.noValidate = true;
      try {
        this.config = JSON.parse(config.textContent);
      } catch (error) {
        return;
      }
      this.variants = this.config.variants || [];
      this.submitButton = this.form.querySelector('button[type="submit"]');
      this.variantInput = this.form.querySelector('[data-wnc-recommendation-id]');
      this.status = this.form.querySelector('[data-wnc-recommendation-status]');
      this.controls = Array.from(this.form.querySelectorAll('input[data-option-index], select[data-option-index]'));
      this.optionIndices = [...new Set(this.controls.map(control => Number(control.dataset.optionIndex)))];
      this.colorIndices = Array.from(this.form.querySelectorAll('[data-color-option]')).map(group => Number(group.dataset.optionIndex));
      this.priority = [...this.colorIndices, ...this.optionIndices.filter(index => !this.colorIndices.includes(index))];
      this.listeners = new AbortController();
      this.form.addEventListener('change', event => this.changeOption(event), { signal: this.listeners.signal });
      this.form.addEventListener('submit', event => this.addToCart(event), { signal: this.listeners.signal });
      this.form.addEventListener('focusin', () => this.closest('card-product-slider')?.slider?.autoplay?.stop(), { signal: this.listeners.signal });
      this.initialized = true;
      this.updateState();
    }

    selections() {
      if (this.config.hasDefaultVariant) return this.variants[0]?.options || [];
      return this.optionIndices.reduce((selected, index) => {
        const control = this.controls.find(item => Number(item.dataset.optionIndex) === index && (item.tagName === 'SELECT' || item.checked));
        selected[index] = control?.value || '';
        return selected;
      }, []);
    }

    static matches(variant, selected, indices = selected.map((value, index) => index)) {
      return indices.every(index => !selected[index] || variant.options[index] === selected[index]);
    }

    static resolveVariant(variants, selected) {
      if (!selected.length || selected.some(value => !value)) return null;
      return variants.find(variant => variant.options.length === selected.length && variant.options.every((value, index) => value === selected[index])) || null;
    }

    static valueAvailable(variants, selected, priority, index, value) {
      const preceding = priority.slice(0, priority.indexOf(index));
      return variants.some(variant => variant.available && variant.options[index] === value && this.matches(variant, selected, preceding));
    }

    changeOption(event) {
      const changed = event.target.closest('[data-option-index]');
      if (!changed || !this.controls.includes(changed)) return;
      const changedIndex = Number(changed.dataset.optionIndex);
      const selected = this.selections();
      const following = this.priority.slice(this.priority.indexOf(changedIndex) + 1);
      following.forEach(index => {
        if (!selected[index] || WncRecommendationCard.valueAvailable(this.variants, selected, this.priority, index, selected[index])) return;
        this.controls.filter(control => Number(control.dataset.optionIndex) === index).forEach(control => {
          if (control.tagName === 'SELECT') control.value = '';
          else control.checked = false;
        });
        selected[index] = '';
      });
      this.setStatus('');
      this.updateState();
    }

    updateState() {
      const selected = this.selections();
      this.controls.forEach(control => {
        const index = Number(control.dataset.optionIndex);
        if (control.tagName === 'SELECT') {
          Array.from(control.options).forEach(option => {
            if (option.value) option.disabled = !WncRecommendationCard.valueAvailable(this.variants, selected, this.priority, index, option.value);
          });
          control.disabled = Boolean(this.busy);
        } else {
          control.disabled = Boolean(this.busy) || !WncRecommendationCard.valueAvailable(this.variants, selected, this.priority, index, control.value);
        }
      });
      this.form.querySelectorAll('[data-color-option]').forEach(group => {
        const name = group.querySelector('[data-wnc-recommendation-color-name]');
        if (name) name.textContent = selected[Number(group.dataset.optionIndex)] || '';
      });
      this.selectedVariant = WncRecommendationCard.resolveVariant(this.variants, selected);
      this.variantInput.value = this.selectedVariant?.available ? this.selectedVariant.id : '';
      const incomplete = this.optionIndices.some(index => !selected[index]);
      this.submitButton.disabled = Boolean(this.busy) || (incomplete ? !this.variants.some(variant => variant.available) : !this.selectedVariant?.available);
      this.submitButton.textContent = this.busy ? this.config.labels.adding : this.variants.some(variant => variant.available) ? this.config.labels.add : this.config.labels.soldOut;
      const preview = this.selectedVariant || this.variants.find(variant => variant.available && WncRecommendationCard.matches(variant, selected));
      if (preview) this.updatePreview(preview);
    }

    money(cents) {
      if (typeof formatPrice === 'function') return formatPrice(cents, this.dataset.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'currency', currency: this.config.currency }).format(Number(cents) / 100);
    }

    updatePreview(variant) {
      const price = this.querySelector('[data-wnc-recommendation-price]');
      const compare = this.querySelector('[data-wnc-recommendation-compare]');
      if (price) price.innerHTML = this.money(variant.price);
      if (compare) {
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.innerHTML = compare.hidden ? '' : this.money(variant.compare_at_price);
      }
      const selected = this.selections();
      const imageVariant = variant.featured_image ? variant : this.variants.find(candidate => candidate.featured_image && this.colorIndices.every(index => candidate.options[index] === selected[index]));
      const image = this.querySelector('.wnc-recommendation-card__image');
      if (image && imageVariant?.featured_image?.src) {
        const src = new URL(imageVariant.featured_image.src, window.location.origin);
        src.searchParams.set('width', '800');
        if (image.dataset.variantSrc !== src.href) {
          image.removeAttribute('srcset');
          image.src = src.href;
          image.dataset.variantSrc = src.href;
        }
      }
      this.querySelectorAll('[data-wnc-recommendation-link]').forEach(link => {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variant.id);
        link.href = url.href;
      });
    }

    setStatus(message, state = '') {
      this.status.textContent = message;
      this.status.dataset.state = state;
    }

    async refreshCart(data, drawer) {
      try {
        if (drawer && data.sections) drawer.renderContents(data, false);
      } catch (error) {
        console.error('Recommendation cart sections could not be refreshed', error);
      }
      if (typeof updateCartCounters === 'function') {
        await updateCartCounters();
      } else {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) {
          const cart = await response.json();
          document.querySelectorAll('.cart-count-badge, .cart-drawer__title-counter').forEach(counter => { counter.textContent = cart.item_count; });
          document.querySelectorAll('.cart-count-badge').forEach(counter => counter.classList.toggle('hidden', cart.item_count === 0));
          document.dispatchEvent(new CustomEvent('wnc:cart-updated', { detail: { cart } }));
        }
      }
      document.dispatchEvent(new CustomEvent('cart-update', { detail: { source: 'wnc-recommendations', productVariantId: this.selectedVariant?.id, cartData: data } }));
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.busy) return;
      const variant = WncRecommendationCard.resolveVariant(this.variants, this.selections());
      if (!variant?.available) {
        this.setStatus(this.config.labels.choose, 'error');
        const selected = this.selections();
        const missing = this.controls.find(control => !selected[Number(control.dataset.optionIndex)]);
        const group = missing?.closest('.wnc-recommendation-card__option');
        if (group) window.wncHighlightSizeGroup?.(group);
        else this.form.reportValidity();
        return;
      }
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.() || [];
      const formData = new FormData(this.form);
      formData.set('id', String(variant.id));
      formData.set('quantity', '1');
      if (this.config.preorder) formData.append('properties[Pre-order]', '✓');
      if (sections.length) {
        formData.set('sections', [...new Set(sections.map(section => section.section))].slice(0, 5).join(','));
        formData.set('sections_url', window.location.pathname);
      }
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      this.setStatus('');
      this.updateState();
      try {
        const root = window.Shopify?.routes?.root || '/';
        const addUrl = typeof routes !== 'undefined' && routes.cart_add_url ? routes.cart_add_url : `${root}cart/add.js`;
        const response = await fetch(addUrl, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
        const data = await response.json();
        if (!response.ok || data.status || data.errors) {
          const detail = data.description || data.message;
          throw new Error(typeof detail === 'string' ? detail : this.config.labels.error);
        }
        this.setStatus(this.config.labels.added, 'success');
        try {
          await this.refreshCart(data, drawer);
        } catch (error) {
          console.error('Recommendation cart counter could not be refreshed', error);
        }
      } catch (error) {
        this.setStatus(error.message || this.config.labels.error, 'error');
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        this.updateState();
      }
    }
  }
  customElements.define('wnc-recommendation-card', WncRecommendationCard);
}
